import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Alert, Table, Badge, Row, Col, Spinner, Modal, Button, Dropdown } from 'react-bootstrap';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { getAllCompaniesPanels } from '../../api/companyApi';
import { listStudents } from '../../api/authApi';
import { getInterviewSchedule, scheduleInterviews, moveInterview, assignInterview, updateInterviewStatus, promoteToHigherRound, getHigherRoundCandidates, scheduleHigherRoundCandidate, markInterviewDone } from '../../api/schedulingApi';
import ConflictModal from './ConflictModal';
import api from '../../utils/axiosConfig';
import { 
  handleUnscheduledToCell, 
  handleCellToCell, 
  handleCellToUnscheduled,
  checkStudentTimeConflict,
  findInterviewInCell
} from '../../utils/schedulingHelpers';

// IMPORTANT: React.StrictMode can cause issues with react-beautiful-dnd
// If drag and drop is not working, temporarily disable StrictMode in src/index.js

// Add CSS to always show scrollbars and drag feedback
const scrollbarStyles = `
  .always-show-scrollbar::-webkit-scrollbar {
    width: 12px;
    height: 12px;
  }
  .always-show-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 6px;
  }
  .always-show-scrollbar::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 6px;
  }
  .always-show-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
  
  /* Drag and drop styling */
  .drop-zone-active {
    background-color: #e3f2fd !important;
    border: 2px dashed #2196f3 !important;
  }
  
  .drop-zone-hover {
    background-color: #bbdefb !important;
  }
  
  .dragging {
    opacity: 0.5;
    transform: rotate(5deg);
  }
  
  .draggable-item {
    cursor: grab;
    transition: all 0.2s ease;
  }
  
  .draggable-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  }
  
  .draggable-item:active {
    cursor: grabbing;
  }
  
  /* Drag and drop styles */
  .dragging {
    opacity: 0.8;
    transform: rotate(5deg);
  }
  
  .drop-zone-active {
    background-color: rgba(0, 123, 255, 0.1) !important;
    border: 2px dashed #007bff !important;
  }
  
  .drop-zone-hover {
    background-color: rgba(0, 123, 255, 0.2) !important;
    border: 2px solid #007bff !important;
  }
`;

const SchedulingDashboard = () => {
  // Load saved state from localStorage
  const loadStoredDate = () => {
    const stored = localStorage.getItem('schedulingDate');
    return stored ? new Date(stored) : new Date();
  };
  
  const loadStoredCompany = () => {
    return localStorage.getItem('schedulingCompany') || '';
  };

  const [companies, setCompanies] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(loadStoredDate());
  const [selectedCompany, setSelectedCompany] = useState(loadStoredCompany());
  const [scheduledInterviews, setScheduledInterviews] = useState([]);
  const [unscheduledStudents, setUnscheduledStudents] = useState([]);
  const [unscheduledReasons, setUnscheduledReasons] = useState({});
  const [higherRoundCandidates, setHigherRoundCandidates] = useState({});
  const [promotedStudents, setPromotedStudents] = useState(new Set());

  // Debug: Monitor higher round candidates changes
  useEffect(() => {
    console.log('Higher round candidates updated:', higherRoundCandidates);
  }, [higherRoundCandidates]);

  // Helper function to check if a student has been promoted
  const isStudentPromoted = (studentId) => {
    const isPromoted = promotedStudents.has(parseInt(studentId));
    console.log('Checking if student promoted:', studentId, isPromoted, 'Promoted students:', Array.from(promotedStudents));
    return isPromoted;
  };
  
  // Helper function to check if a specific interview should be grayed out
  const isInterviewFromPreviousRound = (studentId, currentRoundNumber) => {
    // Find the highest round this student has been promoted to
    let highestRound = 1; // Default to round 1
    
    Object.entries(higherRoundCandidates).forEach(([roundNumber, candidates]) => {
      const roundNum = parseInt(roundNumber);
      const hasStudentInThisRound = candidates.some(candidate => 
        candidate.student.id === parseInt(studentId)
      );
      
      if (hasStudentInThisRound && roundNum > highestRound) {
        highestRound = roundNum;
      }
    });
    
    // Also check if student was just promoted (in promotedStudents set) to round 2
    if (promotedStudents.has(parseInt(studentId))) {
      highestRound = Math.max(highestRound, 2);
    }
    
    // Gray out if current interview is from a round lower than the highest round
    const shouldGrayOut = currentRoundNumber < highestRound;
    console.log('Interview gray check:', {studentId, currentRoundNumber, highestRound, shouldGrayOut});
    return shouldGrayOut;
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [studentFilter, setStudentFilter] = useState('');
  const [unscheduledFilter, setUnscheduledFilter] = useState('');
  const [timeSlotsByRound, setTimeSlotsByRound] = useState({});
  const [loadingMessage, setLoadingMessage] = useState("");
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictInfo, setConflictInfo] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedDate && selectedCompany && companies.length > 0) {
      console.log('UseEffect triggered - fetching schedule data', { selectedDate, selectedCompany, companiesLength: companies.length });
      fetchScheduleData();
    }
  }, [selectedDate, selectedCompany, companies]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch companies and students in parallel
      const [companiesData, studentsData] = await Promise.all([
        getAllCompaniesPanels(),
        listStudents()
      ]);
      
      setCompanies(companiesData || []);
      setStudents(studentsData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch initial data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduleData = async () => {
    try {
      setLoading(true);
  
      const dateStr = selectedDate.toISOString().split('T')[0];
      const companyId = parseInt(selectedCompany);
  
      const response = await getInterviewSchedule(dateStr, companyId);
      console.log('Fetched schedule data:', response);
  
      setScheduledInterviews(response.interviews || []);
      setUnscheduledStudents(response.unscheduled_interviews || []);
      setTimeSlotsByRound(response.time_slots || {}); // Store time slots
      
      // Identify already promoted students from clash_details
      const alreadyPromoted = new Set();
      (response.interviews || []).forEach(interview => {
        console.log('Interview clash_details:', interview.clash_details, 'Student:', interview.student.name);
        if (interview.clash_details && interview.clash_details.includes('Promoted to Round')) {
          console.log('Found promoted student:', interview.student.name, interview.student.id);
          alreadyPromoted.add(parseInt(interview.student.id));
        }
      });
      console.log('Setting promoted students:', Array.from(alreadyPromoted));
      setPromotedStudents(alreadyPromoted);
      
      // Fetch higher round candidates for each round
      const company = companies.find(c => c.id === companyId);
      if (company) {
        const higherRounds = {};
        console.log('Fetching higher round candidates for company:', companyId);
        
        for (let i = 2; i <= 4; i++) {
          try {
            const candidates = await getHigherRoundCandidates(companyId, i);
            console.log(`Round ${i} candidates response:`, candidates);
            
            if (candidates && candidates.unscheduled_interviews && candidates.unscheduled_interviews.length > 0) {
              higherRounds[i] = candidates.unscheduled_interviews;
              console.log(`Found ${candidates.unscheduled_interviews.length} candidates for round ${i}`);
            }
          } catch (err) {
            console.error(`Error fetching candidates for round ${i}:`, err);
          }
        }
        
        console.log('Setting higher round candidates:', higherRounds);
        setHigherRoundCandidates(higherRounds);
      } else {
        console.log('Company not found in companies list');
      }
    } catch (error) {
      console.error('Error fetching schedule data:', error);
      setError('Failed to fetch schedule data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    localStorage.setItem('schedulingDate', date.toISOString());
    setSelectedCompany('');
    localStorage.removeItem('schedulingCompany');
    setScheduledInterviews([]);
    setUnscheduledStudents([]);
  };

  const handleCompanyChange = (e) => {
    const companyId = e.target.value;
    setSelectedCompany(companyId);
    if (companyId) {
      localStorage.setItem('schedulingCompany', companyId);
    } else {
      localStorage.removeItem('schedulingCompany');
    }
  };

  const handleStudentFilterChange = (e) => {
    setStudentFilter(e.target.value);
  };

  const handleUnscheduledFilterChange = (e) => {
    setUnscheduledFilter(e.target.value);
  };



  const getCompaniesForDate = () => {
    if (!selectedDate) return [];
    
    const dateStr = selectedDate.toISOString().split('T')[0];
    return companies.filter(company => company.visit_date === dateStr);
  };

  const getSelectedCompanyDetails = () => {
    if (!selectedCompany) {
      return null;
    }
    return companies.find(c => c.id === parseInt(selectedCompany));
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.includes(':') ? 
      new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      }) : 
      timeString; // If already formatted
  };

  const handleMarkInterviewDone = async (interviewId) => {
    try {
      console.log('Toggling interview completion status:', interviewId);
      const result = await markInterviewDone(interviewId);
      console.log('Mark done result:', result);
      
      toast.success(result.message);
      await fetchScheduleData();
    } catch (error) {
      console.error('Error marking interview done:', error);
      toast.error(error.message || 'Failed to update interview status');
    }
  };

  const handlePromoteToHigherRound = async (studentId, companyId, currentRound) => {
    try {
      console.log('Promoting student:', { studentId, companyId, currentRound });
      const result = await promoteToHigherRound(studentId, companyId, currentRound);
      console.log('Promotion result:', result);
      
      // Add student to promoted students set (ensure consistent type)
      setPromotedStudents(prev => new Set([...prev, parseInt(studentId)]));
      
      toast.success(result.message);
      // Refresh company data to get new rounds without affecting selected company
      console.log('Refreshing company data to get new rounds...');
      const companiesData = await getAllCompaniesPanels();
      setCompanies(companiesData || []);
      
      // Then refresh schedule data
      console.log('Refreshing schedule data after promotion...');
      await fetchScheduleData();
    } catch (error) {
      console.error('Promotion error:', error);
      toast.error(error.message || 'Failed to promote student to higher round');
    }
  };

  const getInterviewsForTimeSlotAndPanel = (round, timeSlot, panel) => {
    // First try to find an interview that matches the exact time slot ID
    let interview = scheduledInterviews.find(
      interview =>
        interview.panel.number === panel.panel_number &&
        interview.time_slot.id === timeSlot.id &&
        interview.status !== 'CANCELLED' && // Exclude cancelled interviews
        interview.status !== 'UNSCHEDULED' // Exclude unscheduled interviews
    );
    
    // If not found, look for interviews matching the time (for cross-round display)
    if (!interview) {
      const matchingInterviews = scheduledInterviews.filter(
        interview =>
          interview.panel.number === panel.panel_number &&
          interview.time_slot.start_time === timeSlot.startTime &&
          interview.time_slot.end_time === timeSlot.endTime &&
          interview.status !== 'CANCELLED' && // Exclude cancelled interviews
          interview.status !== 'UNSCHEDULED' // Exclude unscheduled interviews
      );
      
      // If multiple interviews found (from different rounds), prioritize higher rounds
      if (matchingInterviews.length > 0) {
        matchingInterviews.sort((a, b) => b.round.number - a.round.number);
        interview = matchingInterviews[0];
      }
    }
    
    return interview || null;
  };

  const generateTimeSlots = (round) => {
    if (!round) {
      return [];
    }

    // Convert round.id to string to match the keys in timeSlotsByRound
    const roundId = String(round.id);

    // Get time slots for this round from the API response
    let roundTimeSlots = timeSlotsByRound[roundId] || [];

    // If no time slots are provided by the API, fall back to deriving from scheduledInterviews
    if (roundTimeSlots.length === 0) {
      const seenIds = new Set();
      roundTimeSlots = scheduledInterviews
        .filter(interview => parseInt(interview.round.id) === parseInt(round.id))
        .reduce((acc, interview) => {
          if (!seenIds.has(interview.time_slot.id)) {
            seenIds.add(interview.time_slot.id);
            acc.push({
              id: interview.time_slot.id,
              start_time: interview.time_slot.start_time,
              end_time: interview.time_slot.end_time,
            });
          }
          return acc;
        }, [])
        .sort((a, b) => a.start_time.localeCompare(b.start_time));
    }

    if (roundTimeSlots.length === 0) {
      return [];
    }

    // Map time slots to the format expected by the frontend
    return roundTimeSlots.map(ts => ({
      id: ts.id,
      roundId: round.id,
      roundNumber: round.round_number,
      startTime: ts.start_time,
      endTime: ts.end_time,
    }));
  };

  const handleForceAction = async (action) => {
    setShowConflictModal(false);
    
    try {
      setLoading(true);
      setLoadingMessage("Processing conflict resolution...");
      
      if (action === 'force_schedule' && conflictInfo) {
        // Implement force scheduling logic
        const { student, timeSlot, type, destinationCellId } = conflictInfo;
        
        if (type === 'TIME_CONFLICT') {
          // Force scheduling despite conflict
          const company = getSelectedCompanyDetails();
          const round = company?.rounds?.find(r => r.round_number === timeSlot.roundNumber);
          
          // Extract panel number from destination cell ID or use stored panel info
          let panelNumber;
          if (destinationCellId) {
            const [, , panelNum] = destinationCellId.split('-');
            panelNumber = parseInt(panelNum);
          } else if (conflictInfo.panelNumber) {
            panelNumber = conflictInfo.panelNumber;
          }
          
          const panel = round?.panels?.find(p => p.panel_number === panelNumber);
          
          if (!round || !panel) {
            console.error('Missing data:', { round, panel, panelNumber, timeSlot, company });
            throw new Error(`Invalid round or panel. Round: ${!!round}, Panel: ${!!panel}, Panel Number: ${panelNumber}`);
          }
          
          const assignmentData = {
            student_id: student.id,
            company_id: parseInt(selectedCompany),
            round_id: round.id,
            panel_id: panel.id,
            time_slot_id: timeSlot.id,
            force: true // Tell the API to force scheduling
          };
          
          await assignInterview(assignmentData);
          toast.success(`Forced scheduling of ${student.name} at ${formatTime(timeSlot.startTime)}`);
        }
      } else if (action === 'force_swap' && conflictInfo) {
        // Implement force swap logic
        const { student, existingStudent, timeSlot, sourceTimeSlot, destinationCellId, sourceCellId } = conflictInfo;
        
        // Safety check for cell IDs
        if (!destinationCellId || !sourceCellId) {
          console.error('Missing cell IDs for force swap:', { destinationCellId, sourceCellId, conflictInfo });
          toast.error('Cannot perform force swap: missing cell information');
          return;
        }
        
        // Extract panel information from cell IDs
        const [, destTimeSlotId, destPanelNumber] = destinationCellId.split('-');
        const [, sourceTimeSlotId, sourcePanelNumber] = sourceCellId.split('-');
        
        const company = getSelectedCompanyDetails();
        
        // Find destination round and panel
        const destRound = company?.rounds?.find(r =>
          r.panels.some(p => p.panel_number === parseInt(destPanelNumber))
        );
        const destPanel = destRound?.panels?.find(p => p.panel_number === parseInt(destPanelNumber));
        
        // Find source round and panel
        const sourceRound = company?.rounds?.find(r =>
          r.panels.some(p => p.panel_number === parseInt(sourcePanelNumber))
        );
        const sourcePanel = sourceRound?.panels?.find(p => p.panel_number === parseInt(sourcePanelNumber));
        
        if (!destRound || !destPanel || !sourceRound || !sourcePanel) {
          throw new Error('Invalid swap configuration');
        }
        
        // Find the interview IDs for both students (we'll need to look these up)
        const draggedInterview = scheduledInterviews.find(i => i.student.id === student.id);
        const existingInterview = scheduledInterviews.find(i => i.student.id === existingStudent.id);
        
        if (!draggedInterview || !existingInterview) {
          throw new Error('Could not find interviews for swap');
        }
        
        // Force move both interviews with force=true
        const destMoveData = {
          round_id: destRound.id,
          panel_id: destPanel.id,
          time_slot_id: timeSlot.id,
          force: true
        };
        
        const sourceMoveData = {
          round_id: sourceRound.id,
          panel_id: sourcePanel.id,
          time_slot_id: sourceTimeSlot.id,
          force: true
        };
        
        // Move the dragged interview to destination with force
        await moveInterview(draggedInterview.id, destMoveData);
        
        // Move the existing interview to source with force
        await moveInterview(existingInterview.id, sourceMoveData);
        
        toast.success(`Force swapped ${student.name} with ${existingStudent.name}`);
      }
      
      // Refresh the data
      await fetchScheduleData();
      
    } catch (error) {
      console.error('Error in force action:', error);
      toast.error(error.message || 'Force action failed');
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  // Enhanced drag-and-drop handler with backend sync
  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    
    console.log('=== DRAG END EVENT ===');
    console.log('Full result:', result);
    console.log('Source:', source);
    console.log('Destination:', destination);
    console.log('Draggable ID:', draggableId);
    
    // If there's no destination, do nothing
    if (!destination) {
      console.log('No destination - cancelling drag');
      return;
    }
    
    // Prevent dropping in the same place
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      console.log('Same position - no action needed');
      return;
    }
    
    // Handle dragging from higher round candidates (they come from unscheduled-list but have special draggableId format)
    if (draggableId.startsWith('higher-')) {
      const roundNumber = parseInt(draggableId.split('-')[1]);
      const studentId = draggableId.split('-')[2];
      
      // Find the candidate
      console.log('Looking for higher round candidate:', { roundNumber, studentId });
      console.log('Available higher round candidates:', higherRoundCandidates);
      
      const candidate = higherRoundCandidates[roundNumber]?.find(
        c => c.student.id.toString() === studentId
      );
      
      if (!candidate) {
        console.error('Higher round candidate not found for round', roundNumber, 'student ID', studentId);
        console.error('Available candidates in round', roundNumber, ':', higherRoundCandidates[roundNumber]);
        toast.error('Higher round candidate not found. Please refresh and try again.');
        return;
      }
      
      console.log('Found candidate:', candidate);
      
      // Check if destination is a cell
      if (destination.droppableId.startsWith('cell-')) {
        const [, timeSlotId, panelNumber] = destination.droppableId.split('-');
        const company = getSelectedCompanyDetails();
        
        console.log('=== HIGHER ROUND DRAG DEBUG ===');
        console.log('Company details:', company);
        console.log('Available rounds:', company?.rounds);
        console.log('Looking for round number:', roundNumber);
        console.log('Destination cell ID:', destination.droppableId);
        console.log('Parsed timeSlotId:', timeSlotId, 'panelNumber:', panelNumber);
        
        const round = company?.rounds?.find(r => r.round_number === roundNumber);
        
        if (!round) {
          console.error(`Round ${roundNumber} not found in company.rounds:`, company?.rounds?.map(r => r.round_number));
          toast.error(`Round ${roundNumber} configuration not found`);
          return;
        }
        
        console.log('Round found:', round);
        console.log('Round panels:', round.panels);
        console.log('Looking for panel number:', panelNumber, 'as integer:', parseInt(panelNumber));
        
        const panel = round.panels?.find(p => p.panel_number === parseInt(panelNumber));
        
        // Debug logging
        console.log('Looking for panel:', panelNumber, 'Found panel:', panel);
        console.log('Available timeSlotsByRound keys:', Object.keys(timeSlotsByRound));
        console.log('timeSlotsByRound data:', timeSlotsByRound);
        console.log('Looking for timeSlot with ID:', timeSlotId, 'as integer:', parseInt(timeSlotId));
        console.log('Round details:', { id: round.id, number: round.round_number });
        console.log('Company ID:', company.id);
        
        // Find time slots for this round - use the same logic as generateTimeSlots
        const roundId = String(round.id);
        let roundTimeSlots = timeSlotsByRound[roundId] || [];
        
        console.log(`Looking for time slots with round ID: ${roundId}`);
        console.log('Found round time slots:', roundTimeSlots);
        console.log('Time slot IDs in this round:', roundTimeSlots.map(ts => ts.id));
        
        let timeSlot = roundTimeSlots.find(ts => ts.id === parseInt(timeSlotId));
        console.log('Found matching time slot:', timeSlot);
        
        // If time slot not found in the expected round, search all rounds
        // This handles the case where table shows Round 1 time slots but Round 2 candidates are being scheduled
        if (!timeSlot) {
          console.log('Time slot not found in expected round, searching all rounds...');
          for (const [searchRoundId, searchTimeSlots] of Object.entries(timeSlotsByRound)) {
            const foundSlot = searchTimeSlots.find(ts => ts.id === parseInt(timeSlotId));
            if (foundSlot) {
              timeSlot = foundSlot;
              console.log(`Found time slot ${timeSlotId} in round ${searchRoundId}:`, foundSlot);
              break;
            }
          }
        }
        
        console.log('Final time slot:', timeSlot);
        console.log('=== END DEBUG ===');
        
        if (!panel) {
          console.error('Panel not found for number:', panelNumber);
          console.error('Available panels in round:', round.panels?.map(p => ({ number: p.panel_number, id: p.id })));
          toast.error(`Panel ${panelNumber} not found in Round ${roundNumber}`);
          return;
        }
        
        if (!timeSlot) {
          console.error('Time slot not found for ID:', timeSlotId, 'in any round');
          console.error('All available time slot IDs:', Object.entries(timeSlotsByRound).map(([rid, slots]) => 
            `Round ${rid}: ${slots.map(s => s.id).join(', ')}`
          ));
          toast.error(`Time slot not found for higher round scheduling. Please check console for details.`);
          return;
        }
        
        // Schedule the higher round candidate
        try {
          const payload = {
            student_id: candidate.student.id,
            company_id: company.id,
            round_number: roundNumber,
            panel_id: panel.id,
            time_slot_id: timeSlot.id,
            force_schedule: false
          };
          
          console.log('Scheduling higher round candidate with payload:', payload);
          const response = await scheduleHigherRoundCandidate(payload);
          
          toast.success(response.message);
          await fetchScheduleData();
        } catch (error) {
          if (error.student_conflicts || error.panel_conflict) {
            setConflictInfo({
              ...error,
              attemptedAction: {
                student_id: candidate.student.id,
                company_id: company.id,
                round_number: roundNumber,
                panel_id: panel.id,
                time_slot_id: timeSlot.id
              }
            });
            setShowConflictModal(true);
          } else {
            toast.error(error.message || 'Failed to schedule higher round candidate');
          }
        }
        return;
      }
    }
    
    // Extract the ID from draggableId - handle student, interview, and higher round IDs
    const isUnscheduledDrag = draggableId.startsWith('student-');
    const isScheduledDrag = draggableId.startsWith('interview-');
    const isHigherRoundDrag = draggableId.startsWith('higher-');
    
    if (!isUnscheduledDrag && !isScheduledDrag && !isHigherRoundDrag) {
      console.error('Invalid draggableId format:', draggableId);
      toast.error('Invalid drag operation');
      return;
    }
    
    // For higher round drags, extract the student ID from the format: higher-roundNumber-studentId
    const draggedId = isHigherRoundDrag ? 
      parseInt(draggableId.split('-')[2]) : 
      parseInt(draggableId.split('-')[1]);
    
    if (isNaN(draggedId)) {
      console.error('Invalid ID extracted from draggableId:', draggableId);
      toast.error('Invalid drag operation');
      return;
    }
    
    // Show loading state
    setLoading(true);
    setLoadingMessage(isUnscheduledDrag ? "Scheduling interview..." : "Moving interview...");
    
    try {
      console.log('Drag operation details:', {
        source: source.droppableId,
        destination: destination.droppableId,
        draggedId,
        isUnscheduledDrag,
        isScheduledDrag
      });

      const helpers = {
        getSelectedCompanyDetails,
        generateTimeSlots,
        setConflictInfo,
        setShowConflictModal,
        checkStudentTimeConflict,
        findInterviewInCell,
        updateInterviewStatus,
        assignInterview,
        moveInterview,
        fetchScheduleData,
        formatTime
      };
      
      const data = {
        selectedCompany,
        selectedDate,
        unscheduledStudents,
        scheduledInterviews
      };
      
      // Validate data before proceeding
      if (!selectedCompany || !selectedDate) {
        throw new Error('No company or date selected');
      }
      
      // Handle dragging from unscheduled list to a schedule cell
      if (source.droppableId === 'unscheduled-list' && destination.droppableId.startsWith('cell-')) {
        if (isHigherRoundDrag) {
          // Handle higher round candidate scheduling using the existing logic
          const roundNumber = parseInt(draggableId.split('-')[1]);
          const [, timeSlotId, panelNumber] = destination.droppableId.split('-');
          
          console.log('Handling higher round candidate to cell:', draggableId, '→', destination.droppableId);
          
          // Find the candidate in higherRoundCandidates
          const candidate = higherRoundCandidates[roundNumber]?.find(
            c => c.student.id.toString() === draggedId.toString()
          );
          
          if (!candidate) {
            throw new Error('Higher round candidate not found');
          }
          
          // Use the existing higher round scheduling logic
          const company = getSelectedCompanyDetails();
          const round = company?.rounds?.find(r => r.round_number === roundNumber);
          
          if (!round) {
            throw new Error(`Round ${roundNumber} configuration not found`);
          }
          
          const panel = round.panels?.find(p => p.panel_number === parseInt(panelNumber));
          const roundId = String(round.id);
          let roundTimeSlots = timeSlotsByRound[roundId] || [];
          const timeSlot = roundTimeSlots.find(ts => ts.id === parseInt(timeSlotId));
          
          if (!panel || !timeSlot) {
            throw new Error('Panel or time slot not found for higher round scheduling');
          }
          
          const scheduleData = {
            student_id: candidate.student.id,
            company_id: company.id,
            round_number: roundNumber,
            panel_id: panel.id,
            time_slot_id: timeSlot.id
          };
          
          const result = await scheduleHigherRoundCandidate(scheduleData);
          
          // Backend returns { message: "Interview scheduled successfully", interview: {...} }
          if (result.message === "Interview scheduled successfully" || result.interview) {
            toast.success(`Successfully scheduled ${candidate.student.name} for Round ${roundNumber}`);
            await fetchScheduleData();
          } else {
            throw new Error(result.message || 'Failed to schedule higher round candidate');
          }
        } else if (isUnscheduledDrag) {
          console.log('Handling unscheduled to cell:', draggedId, '→', destination.droppableId);
          await handleUnscheduledToCell(draggedId, destination.droppableId, data, helpers);
        } else {
          throw new Error('Invalid drag: expected student or higher round candidate from unscheduled list');
        }
      } 
      // Handle dragging from a schedule cell to another schedule cell
      else if (source.droppableId.startsWith('cell-') && destination.droppableId.startsWith('cell-')) {
        if (!isScheduledDrag) {
          throw new Error('Invalid drag: expected interview from schedule cell');
        }
        console.log('Handling cell to cell:', draggedId, source.droppableId, '→', destination.droppableId);
        await handleCellToCell(draggedId, source.droppableId, destination.droppableId, data, helpers);
      } 
      // Handle dragging from a schedule cell to the unscheduled list
      else if (source.droppableId.startsWith('cell-') && destination.droppableId === 'unscheduled-list') {
        if (!isScheduledDrag) {
          throw new Error('Invalid drag: expected interview from schedule cell');
        }
        console.log('Handling cell to unscheduled:', draggedId);
        await handleCellToUnscheduled(draggedId, data, helpers);
      }
      else {
        throw new Error(`Unsupported drag operation: ${source.droppableId} → ${destination.droppableId}`);
      }
      
      console.log('Drag operation completed successfully');
    } catch (error) {
      console.error('Scheduling operation failed:', error);
      
      // Check if this is any kind of conflict error that should show modal
      const errorData = error.response?.data || error;
      const statusCode = error.response?.status || error.status;
      
      // Show conflict modal for ANY backend conflict or panel availability error
      if (statusCode === 409 || 
          (errorData.message && errorData.message.toLowerCase().includes('conflict')) ||
          (errorData.message && errorData.message.toLowerCase().includes('panel already has')) ||
          (errorData.message && errorData.message.toLowerCase().includes('scheduled for this time'))) {
        
        // Extract the student and destination info from the drag operation
        let student = null;
        let timeSlot = null;
        let destinationCellId = destination.droppableId;
        
        if (isUnscheduledDrag) {
          student = unscheduledStudents.find(s => s.student.id === draggedId)?.student;
        } else {
          const interview = scheduledInterviews.find(i => i.id === draggedId);
          student = interview?.student;
        }
        
        // Try to extract time slot info from destination
        if (destinationCellId.startsWith('cell-')) {
          const [, timeSlotId, panelNumber] = destinationCellId.split('-');
          
          // Find the time slot from the company rounds
          const company = getSelectedCompanyDetails();
          const round = company?.rounds?.find(r => 
            r.panels.some(p => p.panel_number === parseInt(panelNumber))
          );
          if (round) {
            const roundTimeSlots = generateTimeSlots(round);
            timeSlot = roundTimeSlots.find(ts => ts.id === parseInt(timeSlotId));
          }
        }
        
        if (student && timeSlot) {
          setConflictInfo({
            type: 'TIME_CONFLICT',
            student: student,
            timeSlot: timeSlot,
            conflictDetails: errorData.conflicts || [{ 
              company_name: errorData.message.includes('panel') ? 'Panel Conflict' : 'Time Conflict',
              start_time: timeSlot.startTime,
              end_time: timeSlot.endTime,
              message: errorData.message
            }],
            destinationCellId: destinationCellId,
            errorMessage: errorData.message
          });
          setShowConflictModal(true);
          return;
        }
      }
      
      // For non-conflict errors, show toast
      toast.error(error.message || 'Operation failed. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  // Filter unscheduled students based on unscheduled filter
  const getFilteredUnscheduledStudents = () => {
    // Combine regular unscheduled students with higher round candidates
    let allUnscheduled = [...unscheduledStudents];
    
    // Add higher round candidates to unscheduled list
    Object.entries(higherRoundCandidates).forEach(([roundNumber, candidates]) => {
      candidates.forEach(candidate => {
        // Add round information to student object for display
        allUnscheduled.push({
          id: `higher-${roundNumber}-${candidate.student.id}`,
          student: {
            ...candidate.student,
            name: candidate.student.name || `${candidate.student.user?.first_name} ${candidate.student.user?.last_name}`,
            email: candidate.student.email || candidate.student.user?.email
          },
          roundNumber: parseInt(roundNumber),
          isHigherRound: true
        });
      });
    });
    
    if (!unscheduledFilter.trim()) return allUnscheduled;
    
    return allUnscheduled.filter(item => 
      item.student.name?.toLowerCase().includes(unscheduledFilter.toLowerCase()) ||
      item.student.email?.toLowerCase().includes(unscheduledFilter.toLowerCase())
    );
  };

  // Debug function to check data structure
  const debugDataStructure = () => {
    console.log('=== Debug Data Structure ===');
    console.log('Selected Company:', selectedCompany);
    console.log('Selected Date:', selectedDate);
    console.log('Unscheduled Students:', unscheduledStudents);
    console.log('Scheduled Interviews:', scheduledInterviews);
    console.log('Company Details:', getSelectedCompanyDetails());
    console.log('===========================');
  };

  // Call debug function when data changes
  useEffect(() => {
    if (selectedCompany && selectedDate) {
      debugDataStructure();
    }
  }, [unscheduledStudents, scheduledInterviews]);
  
  return (
    <>
      <style>{scrollbarStyles}</style>
      <Container fluid className="py-2 px-2" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Loading overlay */}
      {loading && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" 
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="bg-white p-4 rounded shadow-lg text-center">
            <Spinner animation="border" role="status" variant="primary" className="mb-3" />
            <h5>{loadingMessage || "Loading..."}</h5>
          </div>
        </div>
      )}

      <div style={{ flex: '0 0 auto' }}>
        <h2 className="mb-2">Interview Scheduling</h2>
        
        {error && <Alert variant="danger">{error}</Alert>}
      </div>
      
      <Card className="mb-2" style={{ flex: '0 0 auto' }}>
        <Card.Header as="h6">Select Company</Card.Header>
        <Card.Body className="py-2">
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Visit Date</Form.Label>
                <DatePicker
                  selected={selectedDate}
                  onChange={handleDateChange}
                  className="form-control"
                  dateFormat="yyyy-MM-dd"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Company</Form.Label>
                <Form.Select 
                  value={selectedCompany}
                  onChange={handleCompanyChange}
                  disabled={getCompaniesForDate().length === 0}
                >
                  <option value="">Select a company</option>
                  {getCompaniesForDate().map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      {selectedCompany && (
        <div style={{ flex: '1 1 auto', overflow: 'hidden' }}>
          <DragDropContext 
            onDragEnd={handleDragEnd} 
            onDragStart={(start) => {
              console.log('Drag started:', start);
            }}
            onDragUpdate={(update) => {
              console.log('Drag update:', update);
            }}
          >
            <Row className="g-2 h-100">
              <Col md={9} className="h-100">
                <Card className="h-100 d-flex flex-column">
                <Card.Header as="h5" className="d-flex justify-content-between align-items-center">
                  <span>Interview Schedule for {getSelectedCompanyDetails()?.name}</span>
                  <Form.Control
                    type="text"
                    placeholder="Filter by student name or email"
                    value={studentFilter}
                    onChange={handleStudentFilterChange}
                    className="ms-auto w-auto"
                  />
                </Card.Header>
                <Card.Body className="always-show-scrollbar" style={{ flex: '1 1 auto', overflowY: 'scroll', overflowX: 'auto' }}>
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status" variant="primary" />
                  <p className="mt-2">Loading schedule...</p>
                </div>
              ) : getSelectedCompanyDetails()?.rounds?.length === 0 ? (
                <Alert variant="warning">No rounds configured for this company.</Alert>
              ) : (() => {
                // Generate unified table with all rounds
                const company = getSelectedCompanyDetails();
                const rounds = company?.rounds || [];
                
                if (rounds.length === 0) {
                  return <Alert variant="warning">No rounds configured for this company.</Alert>;
                }

                // Get all time slots from all rounds and combine them
                let allTimeSlots = [];
                let maxPanels = 0;
                const timeSlotMap = new Map(); // Use Map to deduplicate by time
                
                rounds.forEach(round => {
                  const roundTimeSlots = generateTimeSlots(round);
                  maxPanels = Math.max(maxPanels, round.panels?.length || 0);
                  
                  roundTimeSlots.forEach(timeSlot => {
                    const timeKey = `${timeSlot.startTime}-${timeSlot.endTime}`;
                    
                    // Only add if we haven't seen this time range before
                    // Prioritize Round 1 time slots for consistency
                    if (!timeSlotMap.has(timeKey) || round.round_number === 1) {
                      timeSlotMap.set(timeKey, {
                        ...timeSlot,
                        round: round,
                        panels: round.panels || []
                      });
                    }
                  });
                });
                
                // Convert map to array
                allTimeSlots = Array.from(timeSlotMap.values());

                // Sort by time
                allTimeSlots.sort((a, b) => {
                  const timeA = new Date('1970-01-01T' + a.startTime);
                  const timeB = new Date('1970-01-01T' + b.startTime);
                  return timeA - timeB;
                });

                if (allTimeSlots.length === 0) {
                  return <Alert variant="warning">No time slots available for any rounds.</Alert>;
                }

                return (
                  <div className="mb-4">
                    <h5>Round</h5>
                    <p className="text-muted">
                      Time: {formatTime(allTimeSlots[0]?.startTime)} - {formatTime(allTimeSlots[allTimeSlots.length - 1]?.endTime)} | Max Panels: {maxPanels}
                    </p>
                    
                    <div className="table-responsive">
                      <Table bordered hover>
                        <thead>
                          <tr>
                            <th width="100">Time / Panel</th>
                            {Array.from({ length: maxPanels }, (_, i) => (
                              <th
                                key={`header-panel-${i + 1}`}
                                className="text-center"
                              >
                                Panel {i + 1}
                                <div className="small text-muted">NA</div>
                                <div className="small text-muted">NA</div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {allTimeSlots.map((timeSlot) => (
                            <tr key={`slot-${timeSlot.id}`}>
                              <td className="text-center">
                                {formatTime(timeSlot.startTime)}
                                <br />
                                <span className="text-muted">to</span>
                                <br />
                                {formatTime(timeSlot.endTime)}
                              </td>
                              
                              {Array.from({ length: maxPanels }, (_, panelIndex) => {
                                const panelNumber = panelIndex + 1;
                                const panel = timeSlot.panels.find(p => p.panel_number === panelNumber);
                                
                                if (!panel) {
                                  // Empty cell if no panel exists for this slot
                                  return (
                                    <td key={`slot-${timeSlot.id}-panel-${panelNumber}`} className="align-middle">
                                      <div className="p-2" style={{ minHeight: '60px', backgroundColor: '#f8f9fa' }}></div>
                                    </td>
                                  );
                                }
                                
                                const interview = getInterviewsForTimeSlotAndPanel(
                                  timeSlot.round,
                                  timeSlot,
                                  panel
                                );
                                
                                // Check if this cell matches the student filter
                                const matchesFilter =
                                  studentFilter &&
                                  interview &&
                                  (interview.student.name
                                    .toLowerCase()
                                    .includes(studentFilter.toLowerCase()) ||
                                    interview.student.email
                                      .toLowerCase()
                                      .includes(studentFilter.toLowerCase()));
                                      
                                return (
                                  <td
                                    key={`slot-${timeSlot.id}-panel-${panelNumber}`}
                                    className={`align-middle ${
                                      matchesFilter ? 'bg-warning bg-opacity-25' : ''
                                    }`}
                                  >
                                    {interview ? (
                                      <Droppable
                                        droppableId={`cell-${timeSlot.id}-${panelNumber}`}
                                      >
                                        {(provided, snapshot) => {
                                          const shouldGrayOut = isInterviewFromPreviousRound(interview.student.id, interview.round.number);
                                          const isPromoted = isStudentPromoted(interview.student.id);
                                              return (
                                              <div
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                                className={`p-2 rounded ${
                                                  shouldGrayOut 
                                                    ? 'bg-secondary bg-opacity-25' 
                                                    : 'bg-light'
                                                } ${
                                                  snapshot.isDraggingOver ? 'drop-zone-hover' : ''
                                                }`}
                                                style={{
                                                  opacity: shouldGrayOut ? 0.6 : 1,
                                                  border: shouldGrayOut ? '2px dashed #6c757d' : 'none'
                                                }}
                                              >
                                                <Draggable
                                                  draggableId={`interview-${interview.id}`}
                                                  index={0}
                                                  isDragDisabled={shouldGrayOut}
                                                >
                                                  {(provided, snapshot) => (
                                                    <div
                                                      ref={provided.innerRef}
                                                      {...provided.draggableProps}
                                                      {...provided.dragHandleProps}
                                                      className={`draggable-item ${snapshot.isDragging ? 'dragging' : ''}`}
                                                      style={{
                                                        ...provided.draggableProps.style,
                                                        opacity: shouldGrayOut ? 0.7 : 1
                                                      }}
                                                    >
                                                      <div className="d-flex justify-content-between align-items-start">
                                                        <div className="flex-grow-1">
                                                          <div style={{ 
                                                            textDecoration: shouldGrayOut ? 'line-through' : 'none',
                                                            color: shouldGrayOut ? '#6c757d' : 'inherit'
                                                          }}>
                                                            {interview.student.name}
                                                          </div>
                                                          <div className="text-muted small">
                                                            {interview.student.email}
                                                          </div>
                                                          <div className="d-flex gap-1 mt-1">
                                                            {/* Round badge */}
                                                            <Badge bg="primary" className="small">
                                                              R{interview.round.number}
                                                            </Badge>
                                                            
                                                            {/* Status badge */}
                                                            {shouldGrayOut && interview.clash_details ? (
                                                              <Badge bg="warning" className="small">
                                                                {interview.clash_details}
                                                              </Badge>
                                                            ) : shouldGrayOut ? (
                                                              <Badge bg="secondary">PROMOTED</Badge>
                                                            ) : interview.completion_status === 'DONE' ? (
                                                              <Badge bg="success">DONE</Badge>
                                                            ) : (
                                                              <Badge bg="info">IN PROGRESS</Badge>
                                                            )}
                                                          </div>
                                                        </div>
                                                        {!shouldGrayOut && (
                                                          <Dropdown onClick={(e) => e.stopPropagation()}>
                                                            <Dropdown.Toggle 
                                                              variant="link" 
                                                              size="sm"
                                                              className="p-0 text-muted"
                                                              style={{ fontSize: '0.8rem' }}
                                                            >
                                                              ▼
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu>
                                                              {/* Mark as DONE option */}
                                                              <Dropdown.Item 
                                                                onClick={() => handleMarkInterviewDone(interview.id)}
                                                              >
                                                                {interview.completion_status === 'DONE' ? 'Mark as In Progress' : 'Mark as DONE'}
                                                              </Dropdown.Item>
                                                              
                                                              <Dropdown.Divider />
                                                              
                                                              {/* Send to higher round option */}
                                                              <Dropdown.Item 
                                                                onClick={() => handlePromoteToHigherRound(
                                                                  interview.student.id,
                                                                  interview.company.id,
                                                                  interview.round.number
                                                                )}
                                                              >
                                                                Send to Round {interview.round.number + 1}
                                                              </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                          </Dropdown>
                                                        )}
                                                      </div>
                                                    </div>
                                                  )}
                                                </Draggable>
                                                {provided.placeholder}
                                              </div>
                                            );
                                          }}
                                          </Droppable>
                                        ) : (
                                          <Droppable
                                            droppableId={`cell-${timeSlot.id}-${panelNumber}`}
                                          >
                                            {(provided, snapshot) => (
                                              <div
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                                className={`text-center py-2 ${
                                                  snapshot.isDraggingOver ? 'drop-zone-hover' : ''
                                                }`}
                                                style={{
                                                  minHeight: '60px',
                                                  border: snapshot.isDraggingOver 
                                                    ? '2px dashed #007bff' 
                                                    : '1px dashed #dee2e6'
                                                }}
                                              >
                                                <span className="text-muted small">
                                                  Drag student here
                                                </span>
                                                {provided.placeholder}
                                              </div>
                                            )}
                                          </Droppable>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                    </div>
                  </div>
                );
              })()}
                </Card.Body>
              </Card>
            </Col>
            
            <Col md={3} className="h-100 d-flex flex-column">
              {/* Unscheduled Students Section */}
              <Card className="flex-grow-1 d-flex flex-column">
                <Card.Header className="bg-warning text-dark">
                  <h6 className="mb-2">Unscheduled Students</h6>
                  <Form.Control
                    type="text"
                    placeholder="Search unscheduled students..."
                    value={unscheduledFilter}
                    onChange={handleUnscheduledFilterChange}
                    size="sm"
                  />
                </Card.Header>
                <Card.Body className="p-2 always-show-scrollbar" style={{ flex: '1 1 auto', overflowY: 'scroll' }}>
                  {loading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" role="status" variant="primary" />
                    </div>
                  ) : getFilteredUnscheduledStudents().length === 0 ? (
                    <Alert variant="success">
                      {studentFilter ? 
                        "No unscheduled students match your filter." :
                        "All shortlisted students have been scheduled for interviews."
                      }
                    </Alert>
                  ) : (
                    <Droppable droppableId="unscheduled-list">
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={snapshot.isDraggingOver ? 'drop-zone-active' : ''}
                          style={{
                            minHeight: '100px',
                            border: snapshot.isDraggingOver ? '2px dashed #ffc107' : 'none',
                            borderRadius: '4px',
                            padding: snapshot.isDraggingOver ? '4px' : '0'
                          }}
                        >
                          {getFilteredUnscheduledStudents().map((item, index) => {
                            const reason = item.isHigherRound ? 
                              `Promoted to Round ${item.roundNumber} - awaiting schedule` : 
                              (unscheduledReasons[item.id] || 'Interview marked as unscheduled');
                            
                            const draggableId = item.isHigherRound ? 
                              `higher-${item.roundNumber}-${item.student.id}` : 
                              `student-${item.id}`;
                            
                            return (
                              <div key={item.id} className="mb-2">
                                <Draggable
                                  draggableId={draggableId}
                                  index={index}
                                >
                                  {(provided, snapshot) => (
                                    <Card
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className={`${item.isHigherRound ? 'border-info' : 'border-warning'} draggable-item ${snapshot.isDragging ? 'dragging' : ''}`}
                                      style={{
                                        ...provided.draggableProps.style,
                                        backgroundColor: item.isHigherRound ? '#e3f2fd' : '#fff3cd'
                                      }}
                                    >
                                      <Card.Body className="p-2">
                                        <div className="d-flex justify-content-between align-items-start">
                                          <div className="flex-grow-1">
                                            <div style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                              {item.student.name}
                                            </div>
                                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                              {item.student.email}
                                            </div>
                                          </div>
                                          <Badge bg={item.isHigherRound ? "info" : "warning"} className={item.isHigherRound ? "" : "text-dark"}>
                                            {item.isHigherRound ? `R${item.roundNumber}` : "P3"}
                                          </Badge>
                                        </div>
                                        <p className="mb-1 text-danger" style={{ fontSize: '0.7rem' }}>{reason}</p>
                                        <div className="text-center">
                                          <small className="text-muted">
                                            Drag to schedule
                                          </small>
                                        </div>
                                      </Card.Body>
                                    </Card>
                                  )}
                                </Draggable>
                              </div>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </DragDropContext>
      </div>

    )}


      {/* Conflict modal */}
      <ConflictModal 
        show={showConflictModal}
        handleClose={() => setShowConflictModal(false)}
        conflictInfo={conflictInfo}
        handleForceAction={handleForceAction}
      />
      </Container>
    </>
  );
};

export default SchedulingDashboard;
